// src/pages/GameTypesPage.jsx
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './GameTypesPage.css';

const GameTypesPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { dashboardData } = useAuth();
  
  const gameData = location.state?.gameData;
  const [currentTime, setCurrentTime] = useState(new Date());
  const [openTimeExceeded, setOpenTimeExceeded] = useState(false);

  // Get game types from dashboardData
  const gameTypes = dashboardData?.pana_games_list || [];

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  // Check if open time has exceeded
  useEffect(() => {
    if (!gameData?.open_time) return;

    const now = currentTime;
    const openTime = parseTimeString(gameData.open_time);
    const isOpenTimeExceeded = now >= openTime;

    setOpenTimeExceeded(isOpenTimeExceeded);

    console.log('Time Check:', {
      now: now.toLocaleTimeString(),
      openTime: openTime.toLocaleTimeString(),
      isOpenTimeExceeded
    });

  }, [currentTime, gameData]);

  // Parse time string (e.g., "10:30 AM")
  const parseTimeString = (timeStr) => {
    if (!timeStr) return new Date();
    
    const today = new Date();
    const [time, modifier] = timeStr.split(' ');
    let [hours, minutes] = time.split(':');
    
    hours = parseInt(hours);
    minutes = parseInt(minutes || 0);
    
    if (modifier === 'PM' && hours < 12) hours += 12;
    if (modifier === 'AM' && hours === 12) hours = 0;
    
    const date = new Date(today);
    date.setHours(hours, minutes, 0, 0);
    return date;
  };

  // Check if game should be blocked
  const shouldBlockGame = (gameTypeName) => {
    // Check if it's Jodi Digit, Half Sangam, or Full Sangam
    const isCloseOnlyGame = gameTypeName.includes("Jodi") || 
                           gameTypeName.includes("Half Sangam") || 
                           gameTypeName.includes("Full Sangam");
    
    // Block if it's a close-only game AND open time has exceeded
    return isCloseOnlyGame && openTimeExceeded;
  };

  // Get status message for blocked games
  const getBlockedMessage = () => {
    return `Open time (${gameData?.open_time}) has passed. Betting is closed for Jodi Digit, Half Sangam, and Full Sangam.`;
  };

  // Handle game type click - navigate directly to betting page
  const handleGameTypeClick = (gameType) => {
    // Don't navigate if game is blocked
    if (shouldBlockGame(gameType.game_name)) {
      return;
    }

    // Navigate directly to betting page
    navigate('/bet', {
      state: {
        gameData: gameData,
        selectedGameType: gameType
      }
    });
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="game-types-page">
      {/* HEADER */}
      <div className="game-types-header">
        <button onClick={handleBack} className="back-btn">
          ← Back
        </button>
        <h2>{gameData?.game_name || "Select Game Type"}</h2>
        <div className="header-time">
          <span>Open: {gameData?.open_time || "--"}</span>
          <span>Close: {gameData?.close_time || "--"}</span>
        </div>
      </div>

      {/* BREADCRUMB */}
      <div className="breadcrumb">
        <span>Home</span>
        <span>›</span>
        <span className="active">{gameData?.game_name || "Game"}</span>
      </div>

      {/* WARNING MESSAGE FOR BLOCKED GAMES */}
      {/* {openTimeExceeded && (
        <div className="warning-message">
          <p>⚠️ {getBlockedMessage()}</p>
          <p className="current-time">Current Time: {currentTime.toLocaleTimeString()}</p>
        </div>
      )} */}

      {/* GAME TYPES GRID */}
      <div className="game-types-container">
        <h3>Select Game Type</h3>
        
        {gameTypes.length > 0 ? (
          <div className="game-types-grid">
            {gameTypes.map((gameType, index) => {
              const isBlocked = shouldBlockGame(gameType.game_name);
              const isActive = gameType.status === "1";
              const isDisabled = isBlocked || !isActive;

              return (
                <div 
                  key={index}
                  className={`game-type-card ${isDisabled ? 'disabled' : ''}`}
                  onClick={() => !isDisabled && handleGameTypeClick(gameType)}
                >
                  <div className="game-type-icon">
                    {getGameIcon(gameType.game_name)}
                    {isBlocked && <span className="blocked-overlay">🚫</span>}
                  </div>
                  <div className="game-type-info">
                    <h4>{formatGameName(gameType.game_name)}</h4>
                    <div className="game-type-status-container">
                      {!isActive ? (
                        <span className="game-type-status inactive">● Inactive</span>
                      ) : isBlocked ? (
                        <span className="game-type-status blocked">● Closed</span>
                      ) : (
                        <span className="game-type-status active">● Active</span>
                      )}
                    </div>
                    {isBlocked && (
                      <div className="blocked-message">
                        <small>Open time exceeded</small>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="no-game-types">
            <p>No game types available</p>
          </div>
        )}

        {/* GAME TYPE LEGEND */}
        <div className="game-type-legend">
          <div className="legend-item">
            <span className="legend-color active">●</span>
            <span>Active - Click to bet</span>
          </div>
          <div className="legend-item">
            <span className="legend-color inactive">●</span>
            <span>Inactive - Cannot bet</span>
          </div>
          <div className="legend-item">
            <span className="legend-color blocked">●</span>
            <span>Closed - Open time passed</span>
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <div className="game-types-footer">
        <p>Copyright ©2024 Editor-motto</p>
      </div>
    </div>
  );
};

// Helper function to format game names
const formatGameName = (name) => {
  return name
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};

// Helper function to get icons for game types
const getGameIcon = (gameName) => {
  const icons = {
    'Single Digit': '🔢',
    'Jodi Digit': '➰',
    'Single Pana': '❶',
    'Double Pana': '❷',
    'Triple Pana': '❸',
    'SP Motor': '🏍️',
    'DP Motor': '🏍️',
    'Half Sangam': '½',
    'Full Sangam': 'Ⓐ',
    'Group Jodi': '👥',
    'Red Bracket': '🔴',
    'Panel Group': '📊',
    'default': '🎮'
  };

  for (const [key, icon] of Object.entries(icons)) {
    if (gameName.includes(key)) return icon;
  }
  return icons.default;
};

export default GameTypesPage;